import React, { Component, ErrorInfo, PropsWithChildren } from "react";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";

import { central, palette, radii, semantic, spacing } from "../theme/tokens";
import { recordCentralClientError } from "../lib/firebase";
import { isCentralHost } from "../lib/hosting";

type AppErrorBoundaryState = {
  hasError: boolean;
};

export class AppErrorBoundary extends Component<PropsWithChildren, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void recordCentralClientError("root_render_failure", error.name);
    if (__DEV__) {
      console.error("Central render failure", error, info.componentStack);
    }
  }

  private retry = () => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      const recoveryPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.history.replaceState(window.history.state, "", recoveryPath);
      window.location.reload();
      return;
    }

    this.setState({ hasError: false });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const centralHost = isCentralHost();
    return (
      <View accessibilityRole="alert" style={[styles.page, centralHost ? styles.centralPage : null]}>
        <View style={[styles.panel, centralHost ? styles.centralPanel : null]}>
          <Text style={[styles.eyebrow, centralHost ? styles.centralEyebrow : null]}>CENTRAL</Text>
          <Text accessibilityRole="header" style={[styles.title, centralHost ? styles.centralTitle : null]}>
            We Couldn&apos;t Open This Workspace
          </Text>
          <Text style={[styles.body, centralHost ? styles.centralBody : null]}>
            Your information has not been changed. Refresh the workspace to try again. If the problem continues, contact your Central administrator.
          </Text>
          <Pressable
            accessibilityHint="Reloads Central"
            accessibilityRole="button"
            onPress={this.retry}
            style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
          >
            <Text style={[styles.buttonLabel, centralHost ? styles.centralButtonLabel : null]}>Refresh Workspace</Text>
          </Pressable>
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  page: {
    alignItems: "center",
    backgroundColor: palette.canvas,
    flex: 1,
    justifyContent: "center",
    padding: spacing.lg,
  },
  panel: {
    backgroundColor: semantic.surface.raised,
    borderColor: semantic.border.subtle,
    borderRadius: radii.lg,
    borderWidth: 1,
    maxWidth: 560,
    padding: spacing.xl,
    width: "100%",
  },
  centralPage: {
    backgroundColor: central.surface.canvas,
  },
  centralPanel: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
    borderRadius: central.radius.card,
  },
  eyebrow: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    letterSpacing: 1.2,
    marginBottom: spacing.sm,
  },
  title: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 38,
    lineHeight: 42,
    marginBottom: spacing.sm,
  },
  centralEyebrow: {
    color: central.action.primary,
    fontFamily: central.typography.label,
  },
  centralTitle: {
    color: central.text.primary,
    fontFamily: central.typography.display,
  },
  body: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 16,
    lineHeight: 25,
    marginBottom: spacing.lg,
  },
  centralBody: {
    color: central.text.secondary,
    fontFamily: central.typography.body,
  },
  button: {
    alignItems: "center",
    alignSelf: "flex-start",
    backgroundColor: semantic.action.primary,
    borderRadius: radii.control,
    justifyContent: "center",
    minHeight: 48,
    paddingHorizontal: spacing.lg,
  },
  buttonPressed: {
    backgroundColor: semantic.action.primaryPressed,
  },
  buttonLabel: {
    color: semantic.text.onPrimary,
    fontFamily: "Manrope_700Bold",
    fontSize: 15,
  },
  centralButtonLabel: {
    color: central.text.inverse,
    fontFamily: central.typography.label,
  },
});
